import { useCallback, useRef } from 'react';
import type { MouseEvent, PointerEvent } from 'react';

import isScrolling from '@/utils/interaction/scrollMotion';

/** Pointer travel (px) beyond which a press is a drag, not a tap. */
const TAP_MOVE_TOLERANCE = 10;

export interface TapGestureHandlers {
  onClick: (event: MouseEvent<Element>) => void;
  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;
  onPointerCancel: () => void;
}

/**
 * Fires `onTap` only for a deliberate tap — never for a scroll gesture.
 *
 * Without this, on touch a drag that ends on the element, or a tap used to stop
 * momentum scrolling, still dispatches a `click` and selects something the user
 * never meant to. Two signals separate a scroll from a tap:
 *   - the pointer travels past `TAP_MOVE_TOLERANCE`, or the browser cancels the
 *     pointer to take over scrolling → the press was a drag; and
 *   - the surface is still scrolling at release (touch/pen only) → the press was
 *     a momentum-halting tap. Mouse clicks are exempt so a click right after a
 *     wheel scroll still registers.
 *
 * Activation stays on `click`, so keyboard and assistive tech — which dispatch
 * a click with no pointer sequence — keep working; the pointer handlers only
 * annotate that click.
 *
 * References:
 *  - MDN Pointer events (unified touch/mouse/pen):
 *    https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events
 *  - MDN Using Touch Events — movement threshold to tell a tap from a swipe:
 *    https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Using_Touch_Events
 *  - Embla Carousel `clickAllowed()` — the same "suppress click on drag / while
 *    scrolling" pattern from a battle-tested library:
 *    https://github.com/davidjerleke/embla-carousel/issues/422
 *    https://github.com/davidjerleke/embla-carousel/issues/146
 */
const useTapGesture = (onTap: (() => void) | undefined): TapGestureHandlers => {
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const isDragRef = useRef(false);
  const isCoarsePointerRef = useRef(false);

  const onPointerDown = useCallback((event: PointerEvent) => {
    startRef.current = { x: event.clientX, y: event.clientY };
    isDragRef.current = false;
    isCoarsePointerRef.current = event.pointerType !== 'mouse';
  }, []);

  const onPointerMove = useCallback((event: PointerEvent) => {
    if (!startRef.current || isDragRef.current) return;
    const dx = event.clientX - startRef.current.x;
    const dy = event.clientY - startRef.current.y;
    if (Math.hypot(dx, dy) > TAP_MOVE_TOLERANCE) isDragRef.current = true;
  }, []);

  const onPointerCancel = useCallback(() => {
    isDragRef.current = true;
  }, []);

  const onClick = useCallback(
    (event: MouseEvent<Element>) => {
      const wasDrag = isDragRef.current;
      // Scoped to this element: only a scroll on a surface it lives in can have
      // been the momentum this tap halted. A global check also caught scrolls
      // from elsewhere — including the off-screen `TrendingCarousel`'s 3s
      // auto-advance — and dropped taps the user very much meant.
      const haltedMomentum =
        isCoarsePointerRef.current && isScrolling(event.currentTarget);
      // Reset so a later click without a pointer sequence (keyboard / assistive
      // tech) is never judged by a stale interaction.
      startRef.current = null;
      isDragRef.current = false;
      isCoarsePointerRef.current = false;
      if (wasDrag || haltedMomentum) return;
      onTap?.();
    },
    [onTap]
  );

  return { onClick, onPointerDown, onPointerMove, onPointerCancel };
};

export default useTapGesture;
